import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Slide,
  Skeleton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';
import * as chatsActions from '../../../../redux/actions/chats';
import Chat from '../../../2talk-chat/components/chat';
import Api2Talk from '../../../../services/api2talk';
import {
  getChatClosingStatus,
  getStatusList as getStatusListService,
  getTechList,
  sendMessage as sendMessageService,
  reSendMessage as reSendMessageService,
  sendMessageAttachment,
  sendMessageAudio as sendMessageAudioService,
} from '../../../../services/2talk/omnichannel';
import { PapiroConsole } from '../../../../utils/papiroConsole';
import CustomizedSnackbars from '../../../../components/material-snackbars';
import DialogGeneralSm from '../../../../components/dialog-general-sm';
import DialogGeneralWithAutoComplete from '../../../../components/dialog-general-with-auto-complete';
import DialogGeneralWithCheckbox from '../../../../components/dialog-general-with-checkbox';
import { appendFormDataValue } from '../../../../utils/formDataUtils';

// Transição de slide para o modal (da direita para esquerda)
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

function OmnichannelModal({
  open,
  onClose,
  chatItem,
  userSession,
  chats,
  intl,
  // Redux actions
  addCurrentChat,
  addCurrentOmnichannelChatList,
  addTotalChatCount,
  updateChatStatusId,
  handleChatUserDetailsSidebarToggle,
  handleChatHistorySidebarToggle,
  addCurrentMessages,
  // Callback quando chat for finalizado
  onChatClosed,
}) {
  const [initialLoading, setInitialLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [chatLoaded, setChatLoaded] = useState(false);
  const [statusList, setStatusList] = useState([]);
  const [chatClosingStatus, setChatClosingStatus] = useState([]);
  const [techList, setTechList] = useState([]);

  // Notificacoes
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationVariant, setNotificationVariant] = useState('success');
  const [notificationMessage, setNotificationMessage] = useState('');

  // Modais de confirmacao
  const [dialogRemoveState, setDialogRemoveState] = useState(false);
  const [dialogEndChatState, setDialogEndChatState] = useState(false);
  const [dialogTransferChatState, setDialogTransferChatState] = useState(false);
  const [newStatusValueWaitingConfirm, setNewStatusValueWaitingConfirm] = useState(0);
  const [chatClosingStatusId, setChatClosingStatusId] = useState({ id: 0, name: '' });
  const [techId, setTechId] = useState({ id: 0, name: '' });
  const [createCalledToChat, setCreateCalledToChat] = useState(
    userSession?.user?.automaticallycreatecalledafterendingchat || false
  );

  // Carregar dados iniciais quando o modal abre
  useEffect(() => {
    if (open && chatItem) {
      // Reset states
      setInitialLoading(true);
      setChatLoaded(false);
      loadInitialData();
    }
  }, [open, chatItem?.chatid]);

  // Limpar quando fechar
  useEffect(() => {
    if (!open) {
      addCurrentChat(null);
      addCurrentMessages([]);
      handleChatUserDetailsSidebarToggle(false);
      handleChatHistorySidebarToggle(false);
      setChatLoaded(false);
      setInitialLoading(true);
    }
  }, [open]);

  const loadInitialData = async () => {
    try {
      // Carregar chat no Redux
      const chatLoadedOk = await loadChatData();

      if (chatLoadedOk) {
        // Carregar listas auxiliares
        const [statusResult, closingStatusResult] = await Promise.all([
          getStatusListService(),
          getChatClosingStatus(),
        ]);

        if (statusResult?.success) {
          const filteredStatus = statusResult.data.filter(item => item.id !== 1 && item.id !== 5);
          setStatusList(filteredStatus);
        }

        if (closingStatusResult?.success) {
          setChatClosingStatus(closingStatusResult.data);
        }

        setChatLoaded(true);
      }
    } catch (error) {
      PapiroConsole.log('Erro ao carregar dados iniciais:', error);
      showNotification('error', 'Erro ao carregar dados do chat');
    } finally {
      setInitialLoading(false);
    }
  };

  // Função para iniciar atendimento de chat com status NOVO (1)
  // Chama o endpoint PUT /chat/assigneduser que muda o status de 1 para 2 (Em Andamento)
  const startChatAttendance = async (chatId) => {
    try {
      const sentData = {
        chatId: chatId,
        connectionid: userSession?.connectionid
      };

      PapiroConsole.log('Iniciando atendimento do chat:', chatId);
      const result = await Api2Talk.put('/chat/assigneduser', sentData);

      if (result.data?.success) {
        PapiroConsole.log('Atendimento iniciado com sucesso');
        return true;
      } else {
        const errorMsg = result.data?.errors?.[0] || 'Erro ao iniciar atendimento';
        PapiroConsole.log('Erro ao iniciar atendimento:', errorMsg);
        showNotification('error', errorMsg);
        return false;
      }
    } catch (error) {
      PapiroConsole.log('Erro ao iniciar atendimento:', error);
      showNotification('error', 'Erro ao iniciar atendimento');
      return false;
    }
  };

  const loadChatData = async () => {
    if (!chatItem?.chatid) return false;

    try {
      // Buscar detalhes do chat
      let result = await Api2Talk.get(`/chat/${chatItem.chatid}`);

      if (result.data?.success && result.data.data) {
        let chatData = result.data.data;

        // Se o chat está com status NOVO (1), iniciar atendimento primeiro
        // Isso muda o status de 1 (Novo) para 2 (Em Andamento), salva históricos e envia mensagem automática
        if (chatData.chatstatusid === 1) {
          PapiroConsole.log('Chat com status NOVO, iniciando atendimento...');
          const attendanceStarted = await startChatAttendance(chatItem.chatid);

          if (!attendanceStarted) {
            // Se falhou ao iniciar atendimento, não continua
            return false;
          }

          // Recarregar dados do chat após iniciar atendimento (status mudou de 1 para 2)
          result = await Api2Talk.get(`/chat/${chatItem.chatid}`);
          if (result.data?.success && result.data.data) {
            chatData = result.data.data;
          }
        }

        // IMPORTANTE: Garantir que chatmessages seja sempre um array
        // O ChatSendBar precisa disso para funcionar corretamente
        if (!chatData.chatmessages) {
          chatData.chatmessages = [];
        }

        // Garantir que customer existe (necessário para envio de mensagens)
        if (!chatData.customer) {
          chatData.customer = {
            id: chatData.customerid || 0,
            phone: chatData.customerphone || ''
          };
        }

        // Garantir que todos os campos necessários para o ChatSendBar existam
        // Estes campos são usados em send-bar.js linhas 280-318
        chatData.customerid = chatData.customerid || chatData.customer?.id || 0;
        chatData.channelid = chatData.channelid || 0;
        chatData.techinicalteamid = chatData.techinicalteamid || 0;
        chatData.messageproviderid = chatData.messageproviderid || 0;
        chatData.messageproviderconfigid = chatData.messageproviderconfigid || 0;
        chatData.registrationnumberrequestid = chatData.registrationnumberrequestid || 0;
        chatData.originrequestid = chatData.originrequestid || 0;
        chatData.organizationid = chatData.organizationid || 0;
        chatData.organizationname = chatData.organizationname || '';
        chatData.chatstatusid = chatData.chatstatusid || 0;

        // Garantir que customer.phone existe
        if (chatData.customer && !chatData.customer.phone) {
          chatData.customer.phone = chatData.customerphone || '';
        }

        PapiroConsole.log('Chat data loaded:', chatData);

        addCurrentChat(chatData);
        addCurrentOmnichannelChatList([chatData]);
        addTotalChatCount(1);

        // As mensagens já vêm via API REST em chatData.chatmessages
        // O MessageHub2TalkContext (quando configurado) atualiza automaticamente via Redux
        return true;
      }
      return false;
    } catch (error) {
      PapiroConsole.log('Erro ao carregar chat:', error);
      return false;
    }
  };

  const loadTechList = async () => {
    if (chats?.chat?.id && chats?.chat?.channelid && chats?.chat?.techinicalteamid) {
      const result = await getTechList(
        chats.chat.id,
        chats.chat.channelid,
        chats.chat.techinicalteamid
      );
      if (result?.success) {
        const actualAssignedUserId = chats?.chat?.assigneduserid;
        const list = result.data.filter(c => c.id !== actualAssignedUserId);
        setTechList(list);
      }
    }
  };

  const showNotification = (variant, message) => {
    setNotificationVariant(variant);
    setNotificationMessage(message);
    setOpenNotification(true);
  };

  const closeNotification = () => setOpenNotification(false);

  // === Envio de mensagens ===
  const sendMessage = async (msg, chatid) => {
    const post = {
      message: msg,
      id: chatid,
      techinicalteamid: msg.techinicalteamid || { id: 0, name: '' },
      channelid: msg.channelid || { id: 0, name: '' },
      phonenumberid: msg.phonenumberid || 0,
      connectionid: userSession?.connectionid,
    };

    const result = await sendMessageService(post);
    if (!result?.success) {
      showNotification('error', intl.formatMessage({ id: 'process.error' }));
    }
  };

  const reSendMessage = async (msg, chatid) => {
    const post = {
      message: msg,
      id: chatid,
      techinicalteamid: {},
      channelid: {},
      phonenumberid: {},
      connectionid: userSession?.connectionid,
    };

    const result = await reSendMessageService(post);
    if (!result?.success) {
      showNotification('error', intl.formatMessage({ id: 'process.error' }));
    }
  };

  const sendMessageAttachmentHandler = async (msg, chatid, arrayAttachments, guidList, error = false, errorMessage, attachmentids = []) => {
    if (error) {
      showNotification('error', errorMessage);
      return;
    }

    setActionLoading(true);
    const data = new FormData();
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };

    arrayAttachments.forEach(file => data.append('filesUpload', file));
    guidList.forEach(guid => data.append('GuidList', guid));
    data.append('id', chatid);
    data.append('connectionid', userSession?.connectionid);
    attachmentids.forEach(id => data.append('attachmentids', id));

    // Adicionar channelid e techinicalteamid no nível raiz (BaseVO)
    const channelid = msg?.channelid || { id: 0, name: '' };
    const techinicalteamid = msg?.techinicalteamid || { id: 0, name: '' };
    data.append('channelid[id]', channelid.id || 0);
    data.append('channelid[name]', channelid.name || '');
    data.append('techinicalteamid[id]', techinicalteamid.id || 0);
    data.append('techinicalteamid[name]', techinicalteamid.name || '');

    if (msg) {
      Object.keys(msg).forEach(key => {
        appendFormDataValue(data, `message[${key}]`, msg[key]);
      });
    }

    const result = await sendMessageAttachment(data, config);
    setActionLoading(false);

    if (!result?.success) {
      showNotification('error', result?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
    }
  };

  const sendMessageAudio = async (msg, chatid, audioData) => {
    const data = new FormData();
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };

    const AudioMessage = {
      AudioData: audioData.audioData,
      MimeType: audioData.mimeType,
      Duration: audioData.duration,
      Name: msg.name,
    };

    Object.keys(AudioMessage).forEach(key => {
      data.append(`audiomessage[${key}]`, AudioMessage[key]);
    });

    data.append('id', chatid);
    data.append('connectionid', userSession?.connectionid);

    // Adicionar channelid, techinicalteamid e phonenumberid no nível raiz (BaseVO)
    const channelid = msg?.channelid || { id: 0, name: '' };
    const techinicalteamid = msg?.techinicalteamid || { id: 0, name: '' };
    const phonenumberid = msg?.phonenumberid || { id: 0 };
    data.append('channelid[id]', channelid.id || 0);
    data.append('channelid[name]', channelid.name || '');
    data.append('techinicalteamid[id]', techinicalteamid.id || 0);
    data.append('techinicalteamid[name]', techinicalteamid.name || '');
    data.append('PhoneNumberId[id]', phonenumberid.id || msg?.phonenumberid || 0);

    if (msg) {
      Object.keys(msg).forEach(key => {
        appendFormDataValue(data, `message[${key}]`, msg[key]);
      });
    }

    try {
      const result = await sendMessageAudioService(data, config);

      if (!result?.success) {
        showNotification('error', result?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
      }
    } catch (error) {
      PapiroConsole.log('Erro ao enviar audio:', error);
      showNotification('error', error?.response?.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
    }
  };

  // === Modais de confirmacao ===
  const openOrCloseConfirmationModal = () => setDialogRemoveState(!dialogRemoveState);
  const openOrCloseEndChatModal = () => setDialogEndChatState(!dialogEndChatState);
  const openOrCloseTransferChatModal = () => {
    if (!dialogTransferChatState) {
      loadTechList();
    }
    setDialogTransferChatState(!dialogTransferChatState);
  };

  const changeValues = (stateName, value) => {
    if (stateName === 'statusupdate') {
      if (value !== 0) {
        if (value !== 4) {
          openOrCloseConfirmationModal();
        } else {
          openOrCloseEndChatModal();
        }
        setNewStatusValueWaitingConfirm(value);
      }
    } else if (stateName === 'chatClosingStatusId') {
      setChatClosingStatusId(value);
    } else if (stateName === 'techId') {
      setTechId(value);
    } else if (stateName === 'createCalledToChat') {
      setCreateCalledToChat(value);
    }
  };

  // === Handlers de confirmacao ===
  const handleStatusChangeConfirmation = async () => {
    setActionLoading(true);
    const chatId = chats?.chat?.id;

    if (newStatusValueWaitingConfirm && chatId) {
      try {
        const result = await Api2Talk.put(`/chat/changestatus/${chatId}/${newStatusValueWaitingConfirm}`);

        if (result.data?.success) {
          showNotification('success', 'Status alterado com sucesso');
          const getStatus = statusList.find(item => item.id === newStatusValueWaitingConfirm);
          updateChatStatusId(getStatus, chatId);
          openOrCloseConfirmationModal();
        } else {
          showNotification('error', result.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
        }
      } catch (err) {
        showNotification('error', err.response?.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
      }
    }
    setActionLoading(false);
  };

  const handleEndChatConfirmation = async () => {
    setActionLoading(true);
    const chatId = chats?.chat?.id;

    if (!chatClosingStatusId?.id) {
      showNotification('error', intl.formatMessage({ id: 'end.chat.confirmation.error' }));
      setActionLoading(false);
      return;
    }

    const data = {
      chatid: chatId,
      chatclosingstatusid: chatClosingStatusId.id,
      createcalledtochat: createCalledToChat,
      customerid: chats?.chat?.customer?.id || 0,
    };

    try {
      const result = await Api2Talk.put('/chat/finalizationstatus', data);

      if (result.data?.success) {
        showNotification('success', 'Chat encerrado com sucesso');
        openOrCloseEndChatModal();

        // Notificar que o chat foi fechado
        if (onChatClosed) {
          onChatClosed(chatItem.chatid);
        }

        // Fechar o modal
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        showNotification('error', result.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
      }
    } catch (err) {
      showNotification('error', err.response?.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
    }
    setActionLoading(false);
  };

  const handleTransferChatConfirmation = async () => {
    setActionLoading(true);
    const userId = techId?.id;
    const chatId = chats?.chat?.id;

    if (!userId) {
      showNotification('error', 'Selecione um técnico para transferir');
      setActionLoading(false);
      return;
    }

    const data = {
      chatid: chatId,
      userid: userId,
      connectionid: userSession?.connectionid,
    };

    try {
      const result = await Api2Talk.put('/chat/transferchat', data);

      if (result.data?.success) {
        showNotification('success', 'Chat transferido com sucesso');
        openOrCloseTransferChatModal();

        // Notificar que o chat foi transferido (remove da lista)
        if (onChatClosed) {
          onChatClosed(chatItem.chatid);
        }

        // Fechar o modal
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        showNotification('error', result.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
      }
    } catch (err) {
      showNotification('error', err.response?.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
    }
    setActionLoading(false);
  };

  const initialOption = { id: 0, name: 'Retornar conversa para fila de atendimento' };
  const techListWithInitial = [initialOption, ...techList];

  // Skeleton loading para o chat (sem menu lateral)
  const renderSkeleton = () => (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', flexDirection: 'column', bgcolor: '#fafafa' }}>
      {/* Header do chat */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton variant="circular" width={48} height={48} animation="wave" />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={24} animation="wave" />
          <Skeleton variant="text" width="25%" height={18} animation="wave" />
        </Box>
        <Skeleton variant="rounded" width={100} height={36} animation="wave" />
      </Box>

      {/* Mensagens */}
      <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: i % 2 === 0 ? 'flex-end' : 'flex-start' }}>
            <Skeleton
              variant="rounded"
              width={`${30 + (i * 5)}%`}
              height={50 + (i * 10)}
              animation="wave"
              sx={{ borderRadius: 3 }}
            />
          </Box>
        ))}
      </Box>

      {/* Input de mensagem */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#fff', display: 'flex', gap: 2 }}>
        <Skeleton variant="rounded" width="100%" height={48} animation="wave" sx={{ borderRadius: 3 }} />
        <Skeleton variant="circular" width={48} height={48} animation="wave" />
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        transitionDuration={300}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: '#f5f5f5',
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            margin: 0,
            height: '100vh',
            maxHeight: '100vh',
            width: '700px',
            minWidth: '700px',
            borderRadius: 0,
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          },
        }}
        sx={{
          '& .MuiDialog-container': {
            justifyContent: 'flex-end',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            px: 3,
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Atendimento - Chat #{chatItem?.chatid}
          </Typography>
          <Tooltip title="Fechar" arrow>
            <IconButton
              onClick={onClose}
              sx={{
                color: '#1976d2',
                bgcolor: 'white',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                '&:hover': {
                  bgcolor: '#e3f2fd',
                  border: '2px solid #e3f2fd',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
          {initialLoading ? (
            renderSkeleton()
          ) : chatLoaded && chats?.chat ? (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                height: '100%',
                flex: 1,
                // Forçar o componente omnichannel-chat a ocupar 100%
                '& .omnichannel-chat': {
                  width: '100% !important',
                  minWidth: '100% !important',
                },
              }}
            >
              {/* Area do chat ocupando 100% */}
              <Chat
                  sendMessage={sendMessage}
                  reSendMessage={reSendMessage}
                  sendMessageAttachment={sendMessageAttachmentHandler}
                  sendMessageAudio={sendMessageAudio}
                  statusList={statusList}
                  changeSelect={changeValues}
                  openOrCloseEndChatModal={openOrCloseEndChatModal}
                  openOrCloseTransferChatModal={openOrCloseTransferChatModal}
                  setLoading={setActionLoading}
                  setNotificationMessage={setNotificationMessage}
                  setNotificationVariant={setNotificationVariant}
                  setOpenNotification={setOpenNotification}
                  // Props necessárias para o ChatSendBar funcionar
                  customerid={{
                    id: chats.chat?.customerid || chats.chat?.customer?.id || 0,
                    phone: chats.chat?.customer?.phone || chats.chat?.customerphone || ''
                  }}
                  phonenumberid={{
                    phonenumberid: chats.chat?.registrationnumberrequestid || 0,
                    channelid: chats.chat?.channelid || 0,
                    techinicalteamid: chats.chat?.techinicalteamid || 0,
                    messageproviderid: chats.chat?.messageproviderid || 0,
                    messageproviderconfigid: chats.chat?.messageproviderconfigid || 0,
                    organizationid: chats.chat?.organizationid || 0,
                    organizationname: chats.chat?.organizationname || '',
                    originrequestid: chats.chat?.originrequestid || 0,
                    requeststatusid: chats.chat?.chatstatusid || 0,
                  }}
                  // Forçar largura 100% no modal
                  isMobile={true}
                  // Esconder botões de Informações e Histórico no modal
                  hideExtraActions={true}
                />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                gap: 2,
              }}
            >
              <Typography color="error" variant="h6">
                Erro ao carregar o chat
              </Typography>
              <Typography color="textSecondary">
                Não foi possível carregar os dados do chat. Tente novamente.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de confirmacao de mudanca de status */}
      <DialogGeneralSm
        open={dialogRemoveState}
        title="Confirmar alteração de status"
        text="Deseja realmente alterar o status deste chat?"
        handleConfirm={handleStatusChangeConfirmation}
        handleClose={openOrCloseConfirmationModal}
      />

      {/* Modal de encerramento */}
      <DialogGeneralWithCheckbox
        open={dialogEndChatState}
        dialogTitle={intl.formatMessage({ id: 'end.chat.confirmation' })}
        autocompleteLabel="Selecione o motivo de encerramento"
        chatClosingStatus={chatClosingStatus}
        autocompleteStateName="chatClosingStatusId"
        changeValues={changeValues}
        chechBoxLabel={intl.formatMessage({ id: 'create.called.for.chat' })}
        checkBoxStateName="createCalledToChat"
        checkBoxSelected={createCalledToChat}
        openOrCloseModalFunction={openOrCloseEndChatModal}
        handleConfirmation={handleEndChatConfirmation}
      />

      {/* Modal de transferencia */}
      <DialogGeneralWithAutoComplete
        open={dialogTransferChatState}
        dialogTitle="Transferir conversa"
        autocompleteLabel="Selecione o técnico"
        options={techListWithInitial}
        autocompleteStateName="techId"
        changeValues={changeValues}
        openOrCloseModalFunction={openOrCloseTransferChatModal}
        handleConfirmation={handleTransferChatConfirmation}
      />

      {/* Notificacoes */}
      <CustomizedSnackbars
        variant={notificationVariant}
        message={notificationMessage}
        isOpen={openNotification}
        toClose={closeNotification}
      />

      {/* Loading overlay para ações (enviar mensagem, etc) */}
      {actionLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
}

const mapStateToProps = state => ({
  userSession: state.userSession,
  chats: state.chats,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(chatsActions, dispatch);

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(OmnichannelModal));
